import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import HydrogenSulphideChecklistFormState from "../../../types/forms/Permits/Conditions/HydrogenSulphideChecklistFormState";
import { HYDROGEN_SULPHIDE_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/HydrogenSulphideChecklist";

type HydrogenSulphideChecklistModalProps = {
    checklistVersion: number;
    formState: HydrogenSulphideChecklistFormState;
    onChange: (fn: (prevValue: HydrogenSulphideChecklistFormState) => HydrogenSulphideChecklistFormState) => void;
    resources: Resources;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function HydrogenSulphideChecklistModal({
    checklistVersion,
    formState,
    onChange,
    resources,
    show,
    onHide,
    canManageContent
}: HydrogenSulphideChecklistModalProps) {
    function changeHandler(key: keyof HydrogenSulphideChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: HydrogenSulphideChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("ChecklistHydrogenSulphide")}</h1>
            </Modal.Header>
            <Modal.Body>
                {checklistVersion === HYDROGEN_SULPHIDE_CHECKLIST_VERSION_2026_02 ? (
                    <ChecklistVersion202602
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                ) : (
                    <ChecklistVersion0
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: HydrogenSulphideChecklistFormState;
    canManageContent: boolean;
    changeHandler: (key: keyof HydrogenSulphideChecklistFormState, value: any) => void;
};

function ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-escape-masks-available"
                    labelText={resources.get("IsEscapeMasksAvailable")}
                    onChange={(updatedValue) => changeHandler("isEscapeMasksAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isEscapeMasksAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-portal-hydrogen-sulphide-alarm-available"
                    labelText={resources.get("IsPortalHydroGenSulphideAlarmAvailable")}
                    onChange={(updatedValue) =>
                        changeHandler("isPortalHydroGenSulphideAlarmAvailable", updatedValue)
                    }
                    resources={resources}
                    value={formState.isPortalHydroGenSulphideAlarmAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-wind-direction-thought-of"
                    labelText={resources.get("IsWindDirectionThoughtOf")}
                    onChange={(updatedValue) => changeHandler("isWindDirectionThoughtOf", updatedValue)}
                    resources={resources}
                    value={formState.isWindDirectionThoughtOf.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuos-gas-measurements-in-place"
                    labelText={resources.get("IsContinousGasMeasurementsInPlace")}
                    onChange={(updatedValue) =>
                        changeHandler("isContinousGasMeasurementsInPlace", updatedValue)
                    }
                    resources={resources}
                    value={formState.isContinousGasMeasurementsInPlace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuos-ventilation-in-place"
                    labelText={resources.get("IsContinuousVentilationInPlace")}
                    onChange={(updatedValue) => changeHandler("isContinuousVentilationInPlace", updatedValue)}
                    resources={resources}
                    value={formState.isContinuousVentilationInPlace.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function ChecklistVersion202602({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-fresh-air-mask-needed-during-work"
                    labelText={resources.get("IsFreshAirMaskNeededDuringWork")}
                    onChange={(updatedValue) => changeHandler("isFreshAirMaskNeededDuringWork", updatedValue)}
                    resources={resources}
                    value={formState.isFreshAirMaskNeededDuringWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuous-ventilation-needed"
                    labelText={resources.get("IsContinuousVentilationNeeded")}
                    onChange={(updatedValue) => changeHandler("isContinuousVentilationNeeded", updatedValue)}
                    resources={resources}
                    value={formState.isContinuousVentilationNeeded.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-escape-masks-available-for-all-involved"
                    labelText={resources.get("IsEscapeMasksAvailableForAllInvolved")}
                    onChange={(updatedValue) => changeHandler("isEscapeMasksAvailableForAllInvolved", updatedValue)}
                    resources={resources}
                    value={formState.isEscapeMasksAvailableForAllInvolved.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-hydrogen-sulphide-gas-meter-available"
                    labelText={resources.get("IsHydrogenSulphideGasMeterAvailable")}
                    onChange={(updatedValue) => changeHandler("isHydrogenSulphideGasMeterAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isHydrogenSulphideGasMeterAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-continuous-gas-measurements-in-place-for-entirety-of-work"
                    labelText={resources.get("IsContinousGasMeasurementsInPlaceForEntirityOfWork")}
                    onChange={(updatedValue) => changeHandler("isContinousGasMeasurementsInPlaceForEntirityOfWork", updatedValue)}
                    resources={resources}
                    value={formState.isContinousGasMeasurementsInPlaceForEntirityOfWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-wind-direction-considered-during-work"
                    labelText={resources.get("IsWindDirectionConsideredDuringWork")}
                    onChange={(updatedValue) => changeHandler("isWindDirectionConsideredDuringWork", updatedValue)}
                    resources={resources}
                    value={formState.isWindDirectionConsideredDuringWork.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}


export default HydrogenSulphideChecklistModal;
